import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    // Hidratar el vídeo solo cuando el componente es (casi) visible:
    // - En secciones ocultas por dispositivo (display:none) nunca intersecta,
    //   así que el vídeo del "otro" dispositivo no se descarga jamás.
    // - Bajo el fold, el vídeo carga al acercarse (el póster ya se ve).
    if (this.#hydrated || this.#observer) return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.#hydrate();
        }
      },
      { rootMargin: '200px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }

  #hydrated = false;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  #hydrate() {
    if (this.#hydrated) return;
    this.#hydrated = true;
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
